import { createErrorReportingAsyncThunk } from "../../helpers";
import axios, { getData, isAxiosError } from "../../../axios";
import { createAction, createSlice } from "@reduxjs/toolkit";
import { CargoFormDataWithClientId } from "../../app/cargo-dialog/types";
import * as tPromise from "io-ts-promise";
import { formDataStopToApiStop } from "../../app/cargo-dialog/helpers";
import {
  createClientCargoTemplateReqT,
  editClientCargoTemplateReqT,
  getClientCargoTemplateResT,
  createClientCargoTemplateResT,
  getClientsCargoTemplatesResT,
} from "./types";
import groupBy from "lodash/groupBy";
import { notifyL } from "../../notifications";
import { close } from "../../app/cargo-dialog";
import { selectNewShipmentDateAdjustment } from "../org-settings/selectors";
import { selectInvoiceLinesForCreate } from "../deliveries/selectors";
import { RootState } from "../../../redux-store";
import {
  convertStopUnitsDecimalToString,
  ERR_DELETED_EXTRA_CO2_TYPES,
} from "dora-shared";
import { draftLinesSet } from "../draft-invoice-lines";
import {
  dropDeletedExtraCo2TypesParam,
  isDeletedExtraCo2TypesError,
} from "../extra-co2-types";
import { deliveryT } from "../deliveries/types";

const prefix = "data/client-cargo-templates";

type State = {
  data: Record<string, { id: string; name: string; teamId: string }[]>;
  selectedTemplate: {
    id: string;
    name: string;
    cargoId: string;
    clientId: string;
  } | null;
};

const initialState: State = {
  data: {},
  selectedTemplate: null,
};

const removeTemplateFromState = (state: State, id: string) => {
  for (const clientId in state.data) {
    state.data[clientId] = state.data[clientId].filter((x) => x.id !== id);
  }
};

export const loadClientCargoTemplates = createErrorReportingAsyncThunk(
  `${prefix}/load-cargo-templates-for-client`,
  async () =>
    axios(`/api/client-cargo-templates`)
      .then(getData)
      .then(tPromise.decode(getClientsCargoTemplatesResT))
);

const draftInvoiceLinesForRequest = (state: RootState) =>
  selectInvoiceLinesForCreate()(state).map((l) => ({
    productId: l.productId,
    unitId: l.unitId,
    description: l.description,
    quantity: l.quantity,
    amount: l.amount,
    discountPercentage: l.discountPercentage,
    percentage: l.percentage,
    isFromRoadToll: l.isFromRoadToll,
  }));

// A save adding lines whose extra CO2 type was deleted is refused until the
// user agrees to save without them. Returned, not thrown, so it isn't
// reported as an error; the caller asks and resends with the flag.
const refuseDeletedExtraCo2Types = (err: unknown) => {
  if (isDeletedExtraCo2TypesError(err)) {
    return { refused: ERR_DELETED_EXTRA_CO2_TYPES } as const;
  }
  throw err;
};

export const createClientCargoTemplate = createErrorReportingAsyncThunk(
  `${prefix}/create-client-cargo-template`,
  async (
    input: {
      clientId: string;
      name: string;
      cargoIdToCopyInvoiceLinesFrom: string | null;
      cargoIdToCopyFilesFrom: string | null;
      data: CargoFormDataWithClientId;
      dropDeletedExtraCo2Types?: boolean;
    },
    thunkAPI
  ) => {
    const {
      data,
      name,
      cargoIdToCopyInvoiceLinesFrom,
      cargoIdToCopyFilesFrom,
    } = input;
    const mappedData = mapEditTemplateDataToApiCompatible(data);

    const res = await axios
      .post(
        `/api/client-cargo-templates`,
        createClientCargoTemplateReqT.encode({
          name,
          cargoIdToCopyInvoiceLinesFrom,
          cargoIdToCopyFilesFrom,
          data: {
            ...mappedData,
            // An existing cargo's lines are copied server-side from
            // cargoIdToCopyInvoiceLinesFrom - sending them here as well would
            // duplicate them. A cargo still being created has no id yet, so its
            // lines are the in-memory draft.
            invoiceLines: cargoIdToCopyInvoiceLinesFrom
              ? []
              : draftInvoiceLinesForRequest(thunkAPI.getState()),
          },
        }),
        {
          params: dropDeletedExtraCo2TypesParam(input.dropDeletedExtraCo2Types),
        }
      )
      .then(getData)
      .then(tPromise.decode(createClientCargoTemplateResT))
      .catch(refuseDeletedExtraCo2Types);
    if ("refused" in res) {
      return res;
    }

    thunkAPI.dispatch(
      notifyL({
        type: "success",
        namespace: "notifications",
        key: "clientOrderTemplateCreated",
      })
    );

    return res;
  }
);

export const updateClientCargoTemplate = createErrorReportingAsyncThunk(
  `${prefix}/update-client-cargo-template`,
  async (
    input: {
      clientId: string;
      templateId: string;
      data: CargoFormDataWithClientId;
      // The cargo currently open in the dialog, if it already exists. Its
      // invoice lines live on its delivery, so the server copies them from
      // there - they may not even be loaded client-side yet. Without one, the
      // cargo is still being created and its lines are the in-memory draft.
      cargoId: string | null;
      dropDeletedExtraCo2Types?: boolean;
    },
    thunkAPI
  ) => {
    const { templateId, data, cargoId } = input;
    const mappedData = mapEditTemplateDataToApiCompatible(data);
    const result = await axios
      .put(
        `/api/client-cargo-templates/${templateId}`,
        editClientCargoTemplateReqT.encode(
          cargoId
            ? {
                id: templateId,
                cargoIdToCopyInvoiceLinesFrom: cargoId,
                data: mappedData,
              }
            : {
                id: templateId,
                data: {
                  ...mappedData,
                  invoiceLines: draftInvoiceLinesForRequest(
                    thunkAPI.getState()
                  ),
                },
              }
        ),
        {
          params: dropDeletedExtraCo2TypesParam(input.dropDeletedExtraCo2Types),
        }
      )
      .catch(refuseDeletedExtraCo2Types);
    if ("refused" in result) {
      return result;
    }
    thunkAPI.dispatch(
      notifyL({
        type: "success",
        namespace: "notifications",
        key: "clientOrderTemplateUpdated",
      })
    );
    return result;
  }
);

export const renameClientCargoTemplate = createErrorReportingAsyncThunk(
  `${prefix}/rename-client-cargo-template`,
  async (input: { clientId: string; templateId: string; name: string }) => {
    const { templateId, name } = input;
    return axios.put(`/api/client-cargo-templates/${templateId}/rename`, {
      name,
    });
  }
);

export const deleteClientCargoTemplate = createErrorReportingAsyncThunk(
  `${prefix}/delete-client-cargo-template`,
  async (id: string) => axios.delete(`/api/client-cargo-templates/${id}`)
);

// Dispatched by callers that delete a template through a different duck (the
// edit/create modal's own delete thunk, which does its own DELETE request) so
// this duck's cached list still drops the template — see
// deleteClientCargoTemplate.fulfilled below for the equivalent case when the
// delete happens through this duck instead.
export const clientCargoTemplateRemoved = createAction<{ id: string }>(
  `${prefix}/client-cargo-template-removed`
);

export const loadAndUseClientCargoTemplate = createErrorReportingAsyncThunk(
  `${prefix}/load-and-use-client-cargo-template`,
  async (templateId: string, { getState, dispatch }) => {
    // TODO: when pending block everything: loading
    // TODO: when succeed fill cargo-dialog slice and also here, with selected one
    const newDateAdjustment = selectNewShipmentDateAdjustment(getState());
    const data = await axios
      .get(`/api/client-cargo-templates/${templateId}`)
      .then(getData)
      .then(tPromise.decode(getClientCargoTemplateResT));
    // The template's invoice lines are loaded into the in-memory draft so
    // they show in (and can be edited from) the finance card while creating
    // the new cargo, then persisted together on submit.
    if (data.data.deliveryId) {
      const delivery = await axios
        .get(`/api/deliveries/${data.data.deliveryId}`)
        .then(getData)
        .then(tPromise.decode(deliveryT));
      dispatch(draftLinesSet(delivery.lines));
    }
    return {
      data,
      newDateAdjustment,
    };
  }
);

// Called when opening an existing cargo that has a clientCargoTemplateId, so
// its "created from template X" reference (and the option to update that
// template with the cargo's current data) shows up - without touching the
// draft invoice lines like loadAndUseClientCargoTemplate does, since this
// cargo already has its own delivery/invoice lines.
export const loadCurrentTemplateForCargo = createErrorReportingAsyncThunk(
  `${prefix}/load-current-template-for-cargo`,
  async (templateId: string) => {
    try {
      return await axios
        .get(`/api/client-cargo-templates/${templateId}`)
        .then(getData)
        .then(tPromise.decode(getClientCargoTemplateResT));
    } catch (e) {
      // The template may have been renamed away/deleted since this cargo was
      // created from it - treat that as "no current template" rather than an
      // error to report.
      if (isAxiosError(e) && e.response?.status === 404) {
        return null;
      }
      throw e;
    }
  }
);

export const slice = createSlice({
  name: prefix,
  initialState,
  reducers: {
    clearSelectedTemplate: (state) => {
      state.selectedTemplate = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadClientCargoTemplates.fulfilled, (state, action) => {
        state.data = groupBy(action.payload, "clientId");
      })
      .addCase(createClientCargoTemplate.fulfilled, (state, action) => {
        if ("refused" in action.payload) {
          return;
        }
        const { clientId, name, data } = action.meta.arg;
        const { id, cargoId } = action.payload;
        state.data[clientId] = state.data[clientId] || [];
        state.data[clientId].push({ name, id, teamId: data.assignedTeamId });
        if (!state.selectedTemplate) {
          state.selectedTemplate = { id, name, cargoId, clientId };
        }
      })
      .addCase(updateClientCargoTemplate.fulfilled, (state, action) => {
        if ("refused" in action.payload) {
          return;
        }
        state.data[action.meta.arg.clientId] = state.data[
          action.meta.arg.clientId
        ].map((x) => ({
          ...x,
          teamId:
            x.id !== action.meta.arg.templateId
              ? x.teamId
              : action.meta.arg.data.assignedTeamId,
        }));
      })
      .addCase(deleteClientCargoTemplate.fulfilled, (state, action) => {
        removeTemplateFromState(state, action.meta.arg);
      })
      .addCase(clientCargoTemplateRemoved, (state, action) => {
        removeTemplateFromState(state, action.payload.id);
      })
      .addCase(renameClientCargoTemplate.fulfilled, (state, action) => {
        const { clientId, templateId, name } = action.meta.arg;
        state.data[clientId] = state.data[clientId].map((x) =>
          x.id !== templateId ? x : { ...x, name }
        );
      })
      .addCase(loadAndUseClientCargoTemplate.pending, (state, action) => {
        state.selectedTemplate = null;
        // TODO: loading state on
      })
      .addCase(loadAndUseClientCargoTemplate.rejected, (state, action) => {
        // TODO: loading state off
      })
      .addCase(loadAndUseClientCargoTemplate.fulfilled, (state, action) => {
        // TODO: loading state off
        state.selectedTemplate = {
          id: action.payload.data.id,
          name: action.payload.data.name,
          cargoId: action.payload.data.cargoId,
          clientId: action.payload.data.data.clientId || "",
        };
      })
      .addCase(close, (state) => {
        state.selectedTemplate = null;
      })
      .addCase(loadCurrentTemplateForCargo.fulfilled, (state, action) => {
        state.selectedTemplate = action.payload
          ? {
              id: action.payload.id,
              name: action.payload.name,
              cargoId: action.payload.cargoId,
              clientId: action.payload.data.clientId || "",
            }
          : null;
      });
  },
});

const mapEditTemplateDataToApiCompatible = (
  data: CargoFormDataWithClientId
) => ({
  ...data,
  pickupList: data.pickupList.map(formDataStopToApiStop),
  dropoffList: data.dropoffList.map(formDataStopToApiStop),
  stopUnits: data.stopUnits.map((su) => ({
    ...su,
    ...convertStopUnitsDecimalToString(su),
  })),
});

export const { clearSelectedTemplate } = slice.actions;
export default slice.reducer;
